import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { companyAdminService } from "../../services/api/companyAdminService";
import { planService } from "../../services/api/planService";
import { parsePhoneNumberToParts } from "../../utils/phoneHelpers";
import Select from "../common/Select";

const companySchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  planId: z.string().min(1, "Please select a subscription plan"),
  status: z.string().min(1, "Status is required"),
  phone: z.string().optional(),
  dialCode: z.string().default("1"),
});

const VALID_STATUS_OPTIONS = ["active", "inactive", "blocked"];

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Blocked", value: "blocked" },
];

const CompanyModal = ({
  show,
  onHide,
  modalMode = "add",
  companyData,
  onSuccess,
}) => {
  const [plans, setPlans] = useState([]);
  const [fetchedAdmin, setFetchedAdmin] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(companySchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      planId: "",
      status: "active",
      phone: "",
      dialCode: "1",
    },
  });

  const fetchPlans = useCallback(async () => {
    try {
      const data = await planService.list({ limit: 100, isActive: true });
      if (data?.results) {
        setPlans(data.results);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    const initialize = async () => {
      await fetchPlans();

      let adminData = companyData;

      if (modalMode === "edit" && companyData?._id) {
        try {
          const fresh = await companyAdminService.getById(companyData._id);
          if (fresh?._id) {
            setFetchedAdmin(fresh);
            adminData = fresh;
          }
        } catch (error) {
          console.error("Error fetching admin details:", error);
        }
      }

      if (modalMode === "edit" && adminData) {
        let planIdValue = "";
        const p = adminData.planId || adminData.plan;
        if (p && typeof p === "object") {
          planIdValue = p.id || p._id || "";
        } else {
          planIdValue = p || "";
        }

        const phoneNumber = adminData.contact || adminData.phone || "";
        const parsedPhone = parsePhoneNumberToParts(phoneNumber);
        const fullPhone = parsedPhone.countryCode && parsedPhone.contact
          ? `${parsedPhone.countryCode}${parsedPhone.contact}`
          : phoneNumber;
        const rawStatus = adminData.status || "active";
        const normalizedStatus = VALID_STATUS_OPTIONS.includes(rawStatus)
          ? rawStatus
          : adminData.isActive
            ? "active"
            : "inactive";

        reset({
          firstName: adminData.firstName || "",
          lastName: adminData.lastName || "",
          email: adminData.email || "",
          status: normalizedStatus,
          planId: planIdValue,
          phone: fullPhone,
          dialCode: (parsedPhone.countryCode || "+1").replace(/\+/g, ""),
        });
      } else {
        reset({
          firstName: "",
          lastName: "",
          email: "",
          planId: "",
          status: "active",
          phone: "",
          dialCode: "1",
        });
        setFetchedAdmin(null);
      }
    };

    initialize();
  }, [show, modalMode, companyData, reset, fetchPlans]);

  const onSubmit = async (data) => {
    try {
      const { countryCode, contact } = parsePhoneNumberToParts(data.phone)

      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        contact: contact || "",
        countryCode: countryCode || "+92",
      };

      if (modalMode !== "edit") payload.planId = data.planId;
      if (modalMode === "edit") payload.status = data.status;

      const response = await (modalMode === "edit"
        ? companyAdminService.update(
            companyData._id || companyData.id,
            payload
          )
        : companyAdminService.create(payload));

      if (response?._id || response?.user || response?.message) {
        toast.success(
          `Admin ${modalMode === "edit" ? "updated" : "created"} successfully`,
        );
        onSuccess?.();
        onHide();
      } else {
        toast.error(response?.message || "Failed to save");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.body?.status ||
        error.message ||
        "An error occurred";
      toast.error(errorMessage);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[1100]"
      onClick={onHide}
    >
      <div
        className="relative p-[1px] w-full max-w-[500px] min-w-[300px] rounded-[12px] bg-gradient-to-br from-[#0058E8] to-[#00318200]"
        onClick={(e) => e.stopPropagation()}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white rounded-[12px] shadow-lg p-6 max-h-[90vh] overflow-y-auto"
        >
          <h2 className="text-xl text-text-dark mb-5 font-semibold">
            {modalMode === "add"
              ? "Add New Company Admin"
              : "Edit Company Admin"}
          </h2>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-text-secondary mb-1 block">
                  First Name *
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      placeholder="John"
                      className="w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                />
                {errors.firstName && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.firstName.message}
                  </span>
                )}
              </div>

              <div>
                <label className="text-sm text-text-secondary mb-1 block">
                  Last Name *
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      placeholder="Doe"
                      className="w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                />
                {errors.lastName && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.lastName.message}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Email *
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="email"
                    placeholder="john@company.com"
                    readOnly={modalMode === "edit"}
                    className={`w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                      modalMode === "edit"
                        ? "bg-gray-100 text-text-secondary cursor-not-allowed focus:ring-0"
                        : ""
                    }`}
                  />
                )}
              />
              {errors.email && (
                <span className="text-red-500 text-xs mt-1 block">
                  {errors.email.message}
                </span>
              )}
            </div>

            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Phone Number
              </label>
              <div className="relative border border-border-light rounded-lg p-2">
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      international
                      defaultCountry="PK"
                      value={field.value || ''}
                      onChange={(value) => {
                        setValue('phone', value || '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      onBlur={() => {
                        setValue('phone', field.value || '', {
                          shouldValidate: true,
                        });
                      }}
                      placeholder="Enter phone number"
                      className="w-full"
                    />
                  )}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>
              )}
            </div>

            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Subscription Plan *
              </label>
              <Controller
                name="planId"
                control={control}
                render={({ field }) => (
                  <Select {...field} wrapperClassName="">
                    <option value="" disabled>
                      Select Plan
                    </option>
                    {plans.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.name} - ${plan.price}/{plan.billingCycle}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.planId && (
                <span className="text-red-500 text-xs mt-1 block">
                  {errors.planId.message}
                </span>
              )}
            </div>

            {modalMode === "edit" && (
              <div>
                <label className="text-sm text-text-secondary mb-1 block">
                  Status *
                </label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} wrapperClassName="">
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </div>
            )}

            <hr className="w-full border-t border-gray-200 mt-3" />

            <div className="flex gap-3 mt-2 justify-end">
              <button
                type="button"
                className="text-sm font-semibold text-text-dark bg-white border border-gray-200 py-3 px-6 rounded-lg"
                onClick={onHide}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-sm font-semibold bg-primary border border-primary text-white py-3 px-8 rounded-lg disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : modalMode === "edit"
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>


    </div>
  );
};

export default CompanyModal;
